import { useEffect, useMemo, useRef, useState } from "react";
import "./App.css";
import Chessboard from "./Chessboard";
import { Chess, Move } from "chess.js";
import { getLichessOpeningData, getOpeningBookMove, LichessResponse } from "./getOpeningBookMove";
import { useForceUpdate, usePrevious, useViewportSize } from "@mantine/hooks";
import moveSelfSound from "./assets/sound-effects/move-self.mp3";
import moveOpponentSound from "./assets/sound-effects/move-opponent.mp3";
import capturedSound from "./assets/sound-effects/capture.mp3";
import castleSound from "./assets/sound-effects/castle.mp3";
import checkSound from "./assets/sound-effects/move-check.mp3";
import successSound from "./assets/sound-effects/success.mp3";
import whooshSound from "./assets/sound-effects/whoosh.mp4";
import useSound from "use-sound";
import { Attempt, EndReason, Mode, Position, ShouldReset, State } from "./models";
import MenuPanel from "./MenuPanel";
import PlayingPanel from "./PlayingPanel";
import { Affix, Alert, Burger, Button, rem } from "@mantine/core";
import PlayingPanelMobile from "./PlayingPanelMobile";
import useKeypress from "react-use-keypress";
import { getUser, paywall, postLoggedOutAttempts } from "./Authentication";
import PurchaseConfirmationModal from "./Stripe/PurchaseConfirmationModal";
import { getShouldEndRun } from "./shouldEndRun";
import {
    EQUALIBRIUM,
    getAttemptType,
    getCanWinTrophies,
    getEngineLevelForPosition,
    getKFactor,
    getRatingGain,
    isInPlayingState,
    setJudementsForEvaluations,
    sleep,
} from "./utils";
import {
    useLoggedOutStorage,
    useMistakesStorage,
    usePersistedStorage,
    useSettingsStorage,
    useStorage,
} from "./ZustandState";
import { postAttempt } from "./backendEndpoints";
import { fetchPositionsForMenu } from "./positions/fetchPositions";
import { useQuery } from "react-query";
import _ from "lodash";
import ContextMenu from "./ContextMenu";
import FirstGameModal from "./FirstGameModal";
import { useSearchParams } from "react-router-dom";
import { fetchStats } from "./Stats";
import Header from "./Header";
import * as Sentry from "@sentry/react";
import { isCurrentPositionInRepo } from "./customRepo";
import cn from "classnames";
import Engine from "./engine";
import { IconTriangle } from "@tabler/icons-react";

const ENEMY_MOVE_TIME = 650;
const SLEEP_TIME_BEFORE_END_ON_PLAYERS_TURN = 400;
const SLEEP_TIME_BEFORE_END_ON_OPPONENTS_TURN = 400;
const CLOUD_EVAL_ENABLED = true;
export const END_GAME_AFTER_3_MOVES = false;
// const PGN_TO_REPEAT =
//     "1. e4 {0.2} c5 {0.2} 2. c3 {0.0} e5 {0.3} 3. Nf3 {0.8} d6 4. d4 {0.7} cxd4 {0.9} 5. cxd4 {0.9} Nd7 6. Nc3 {0.9} exd4 {1.7}";
const PGN_TO_REPEAT = "";

const App: React.FC = () => {
    const [game, setGame] = useState<Chess>();
    const [playMoveSelfSound] = useSound(moveSelfSound);
    const [playMoveOpponentSound] = useSound(moveOpponentSound);
    const [playCapturedSound] = useSound(capturedSound);
    const [playCastleSound] = useSound(castleSound);
    const [playCheckSound] = useSound(checkSound);
    const [playSuccessSound] = useSound(successSound);
    const [playWhooshSound] = useSound(whooshSound, { volume: 0.15 });
    const [state, setState] = useState<State>("menu");
    const [mode, setMode] = useState<Mode>("rated");
    const [shouldReset, setShouldReset] = useState<ShouldReset>({ shouldReset: false });
    const setAttempts = useStorage((state) => state.setAttempts);
    const attempts = useStorage((state) => state.attempts);
    const levels = useStorage((state) => state.levels);
    const setPlayerColor = useStorage((state) => state.setPlayerColor);
    const playerColor = useStorage((state) => state.playerColor);
    const setSelectedPositionID = usePersistedStorage((state) => state.setSelectedPositionID);
    const selectedPositionID = usePersistedStorage((state) => state.selectedPositionID);
    const settings = useSettingsStorage((state) => state);
    const setLevels = useStorage((state) => state.setLevels);
    const setArrowToDraw = useStorage((state) => state.setArrowToDraw);
    const arrowToDraw = useStorage((state) => state.arrowToDraw);
    const [hasFetchedUser, setHasFetchedUser] = useState(false);
    const [ratingGain, setRatingGain] = useState<number | undefined>(undefined);
    const [isRepeatingLine, setIsRepeatingLine] = useState(false);
    const [isUnrated, setIsUnrated] = useState(false);
    const loggedOutData = useLoggedOutStorage((state) => state);
    const setLoggedOutData = useLoggedOutStorage((state) => state.setLoggedOutData);
    const opponentRatingPerMove = useRef<number[]>([]);
    const mistakesStorage = useMistakesStorage((state) => state);
    const setUniquePositionAgainstPlayer = useStorage((state) => state.setUniquePositionAgainstPlayer);
    const uniquePositionAgainstPlayer = useStorage((state) => state.uniquePositionAgainstPlayer);
    const openedSettings = useStorage((state) => state.openedSettings);
    const setMoveHistory = useStorage((state) => state.setMoveHistory);
    const addEvaluation = useStorage((state) => state.addEvaluation);
    const setEvaluations = useStorage((state) => state.setEvaluations);
    const redoStack = useStorage((state) => state.redoStack);
    const setRedoStack = useStorage((state) => state.setRedoStack);
    const [lichessDataState, setLichessDataState] = useState<LichessResponse>();
    const evaluations = useStorage((state) => state.evaluations);
    const setBestMoves = useStorage((state) => state.setBestMoves);
    const addBestMove = useStorage((state) => state.addBestMove);
    const setStockfishEval = useStorage((state) => state.setStockfishEval);
    const setShowFirstGameModal = useStorage((state) => state.setShowFirstGameModal);
    const storage = useStorage((state) => state);

    const { data: user, isLoading: isLoadingUser } = useQuery(["user"], () => getUser());

    const [searchParams, setSearchParams] = useSearchParams();
    const registered = searchParams.get("registered") || undefined;
    const engine = useMemo(() => new Engine(), []);
    // Separate engine instance used exclusively for per-move position evaluations
    const evalEngine = useMemo(() => new Engine(), []);

    const {
        data: positionsFromQuery,
        error,
        isLoading,
    } = useQuery(
        ["positionsData", user, settings.startRating],
        () => fetchPositionsForMenu(user, settings, loggedOutData),
        {
            enabled: !isLoadingUser,
        },
    );

    const [positions, setPositions] = useState(positionsFromQuery);

    useEffect(() => {
        // if it has a new value then set it
        if (!_.isEqual(positions, positionsFromQuery) && positionsFromQuery) {
            setPositions(positionsFromQuery);
        }
    }, [positionsFromQuery]);

    const selectedPosition: Position | undefined = positions?.find((pos) => pos.id === selectedPositionID);
    const positionsToDisplay = positions?.filter((pos) => pos.defaultPlayerColor === playerColor);

    const previousSelectedPosition = usePrevious(selectedPosition);

    // If cached rating is different from real one, then update selectedPosition
    const realPosition = positionsToDisplay?.find((pos) => pos.id === selectedPosition?.id);
    if (realPosition && realPosition.rating !== selectedPosition?.rating) {
        // setSelectedPosition(realPosition);
    }
    const { width } = useViewportSize();
    const forceUpdate = useForceUpdate();

    const initialFen = "rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1";

    const rejectionThreshold = settings.rejectionThreshold;

    // For when a user has been premium before, but no longer is
    // useEffect(() => {
    //     if (!selectedPosition?.isInitialPosition && user && !user?.isPremium && positionsToDisplay) {
    //         setSelectedPositionID(
    //             (
    //                 positionsToDisplay?.find((pos) => pos.isInitialPosition) ||
    //                 positionsToDisplay?.[0] ||
    //                 selectedPosition!
    //             ).id
    //         );
    //     }
    // }, [selectedPosition, user, positionsToDisplay]);

    useEffect(() => {
        if (selectedPosition?.defaultPlayerColor === playerColor) {
            return;
        }

        if (positionsToDisplay) {
            setSelectedPositionID(positionsToDisplay[0].id);
        }
    }, [playerColor]);

    // When logging out we need to reset selectedPositionID
    useEffect(() => {
        if (positions && !positions.map((pos) => pos.id).includes(selectedPositionID) && positionsToDisplay) {
            if (positionsToDisplay.length === 0) {
                // Redirect to positions
                window.location.href = "/positions";
            }
            setSelectedPositionID(positionsToDisplay[0].id);
        }
    }, [positionsToDisplay, selectedPositionID]);

    useEffect(() => {
        const positionId = searchParams.get("position") || undefined;
        const mistakesId = searchParams.get("mistakes") || undefined;
        if ((positionId || mistakesId) && positions) {
            setTimeout(() => {
                if (mistakesId) {
                    setMode("mistakes");
                    setIsUnrated(true);
                    setPlayerColor(mistakesStorage.mistakeAttempts[0].playerColor);
                }
                const foundPosition = positions?.find(
                    (pos) => pos.id === parseInt(positionId! || mistakesId!),
                ) as Position;
                setSelectedPositionID(foundPosition.id);
                setPlayerColor(foundPosition.defaultPlayerColor || "w");
                setState("playing");
                searchParams.delete("position");
                searchParams.delete("mistakes");
                setSearchParams(searchParams);
                const newGame = new Chess(foundPosition.fen);
                if (foundPosition.pgn) {
                    newGame.loadPgn(foundPosition.pgn);
                }

                // Copy/paste from handleOnStart (cannot call that function due to some state weirdness)
                setRatingGain(undefined);
                setRedoStack([]);
                setMoveHistory([]);
                setEvaluations({});
                setBestMoves([]);
                setUniquePositionAgainstPlayer(false);
                opponentRatingPerMove.current = [];
                setState("playing");
                const history = newGame?.history({ verbose: true });
                setMoveHistory(history!);

                if (settings.preferDubious && _.intersection(["2500", "Masters"], levels).length > 0) {
                    alert("Trappy/Dubious moves setting is only available for opponent levels up to 2200");
                    return;
                }

                if (state !== "attempt_finished" && shouldReset.reason !== "wrongMove") {
                    setAttempts([]);
                }

                if ((mode === "rated" || mode === "mistakes") && settings.isRated) {
                    const engineLevel = getEngineLevelForPosition(foundPosition.rating || 1000);
                    setLevels([engineLevel]);
                }
                if (settings.startFromInitial) {
                    newGame?.load(initialFen);
                }

                setGame(newGame);
            }, 400);
        }
    }, [positions]);

    useEffect(() => {
        const newGame = new Chess(initialFen);
        setGame(newGame);
        if (newGame.turn() === "b") {
            setPlayerColor("b");
        }
    }, []);

    useEffect(() => {
        if (selectedPosition && selectedPosition?.id !== previousSelectedPosition?.id) {
            handleOnResetPosition();
        }
    }, [selectedPosition]);

    useEffect(() => {
        if (game?.turn() !== playerColor && state === "playing") {
            doEngineMove();
        }
    }, [state]);

    useEffect(() => {
        if (mode === "rated" && !getCanWinTrophies(settings)) {
            setMode("practice");
        }
    }, [settings]);

    useEffect(() => {
        setHasFetchedUser(true);
    }, [user]);

    useEffect(() => {
        if (uniquePositionAgainstPlayer) {
            playWhooshSound();
        }
    }, [uniquePositionAgainstPlayer]);

    useEffect(() => {
        setJudementsForEvaluations(evaluations, playerColor);
    }, [evaluations]);

    // useEffect(() => {
    //     if (state === "attempt_finished") {
    //         setJudementsForEvaluations(evaluations, playerColor);
    //     }
    // }, [state]);

    useEffect(() => {
        const func = async () => {
            if (registered === "true") {
                const stats = await fetchStats();
                const totalNumberOfAttempts = stats.positionStats.flatMap((pos) => pos.OtAttempt2).length;
                const loggedOutAttempts = [...loggedOutData.whiteAttempts, ...loggedOutData.blackAttempts];
                if (totalNumberOfAttempts === 0 && !_.isEmpty(loggedOutAttempts)) {
                    await postLoggedOutAttempts(loggedOutAttempts);
                    await setLoggedOutData({
                        ...loggedOutData,
                        whiteAttempts: [],
                        blackAttempts: [],
                        whiteRating: settings.startRating,
                        blackRating: settings.startRating,
                    });
                }
                searchParams.delete("registered");
                setSearchParams(searchParams);
            }
        };

        func();
    }, [registered]);

    const setBestMoveArrow = () => {
        const currentEntry = Object.entries(evaluations).find(([, evalObj]) => evalObj.fen === game?.fen());
        if (!currentEntry) {
            setArrowToDraw(undefined);
            forceUpdate();
            return;
        }
        const moveHalfNum = Number(currentEntry[0]);
        const moveInEvaluations = evaluations[moveHalfNum];
        const previousMove = evaluations[moveHalfNum - 1];
        const nextMove = evaluations[moveHalfNum + 1];
        if (moveInEvaluations?.judgement && previousMove?.bestMove) {
            const firstMove = previousMove.bestMove.slice(0, 2);
            const secondMove = previousMove.bestMove.slice(2, 4);
            setArrowToDraw({
                from: firstMove,
                to: secondMove,
            });
        } else if (nextMove?.judgement && moveInEvaluations.bestMove) {
            const firstMove = moveInEvaluations.bestMove.slice(0, 2);
            const secondMove = moveInEvaluations.bestMove.slice(2, 4);
            setArrowToDraw({
                from: firstMove,
                to: secondMove,
            });
        } else {
            setArrowToDraw(undefined);
        }
        forceUpdate();
    };

    useKeypress(["ArrowLeft", "ArrowRight"], (event: any) => {
        if (openedSettings) {
            return;
        }

        if ((mode === "practice" && state === "playing") || state === "attempt_finished")
            if (event.key === "ArrowLeft") {
                const lastMove = _.last(game?.history({ verbose: true }));
                if (lastMove) {
                    setRedoStack([...redoStack, lastMove!]);
                    game?.undo();

                    setBestMoveArrow();
                    forceUpdate();
                }
            } else {
                const lastMove = _.last(redoStack);
                if (lastMove) {
                    game?.move(lastMove.san);
                    // game?.setComment(lastMove.comment!);
                    setRedoStack(redoStack.slice(0, redoStack.length - 1));
                    setBestMoveArrow();
                    forceUpdate();
                }
            }
    });

    useKeypress("r", () => {
        if (openedSettings) {
            return;
        }

        if (mode === "practice" && state === "playing") {
            handleOnStart();
        }
    });

    useKeypress("b", () => {
        handleOnBack();
    });

    useKeypress("a", () => {
        if (openedSettings) {
            return;
        }

        if (import.meta.env.DEV) {
            game!.loadPgn(
                "1. e4 {0.2} e6 2. d4 {0.3} d5 3. Nd2 {0.2} c5 4. c3 {-0.1} Nc6 {0.0} 5. Ndf3 {-1.6} Nf6 {-0.2} 6. Bd3 {-4.7} cxd4 {-0.1} 7. cxd4",
            );
            setState("attempt_finished");
            setMoveHistory([...game!.history({ verbose: true })]);
        }
    });

    useKeypress("p", () => {
        if (openedSettings) {
            return;
        }
        if (import.meta.env.DEV) {
            setState("playing");
        }
    });

    useKeypress("f", () => {
        if (openedSettings) {
            return;
        }
        if (state === "menu") {
            setPlayerColor(playerColor === "w" ? "b" : "w");
        }
    });

    // console.log(evaluations);

    useEffect(() => {
        let savedEvalData: any = undefined;

        engine.onMessage((response) => {
            if (response.bestMove) {
                const { positionEvaluation, depth, halfMoveNumber, fen, time, nps, lastMove } = savedEvalData;

                addBestMove(response.bestMove);
                // setStockfishEval({
                //     positionEvaluation,
                //     depth,
                //     time,
                //     nps,
                //     isComplete: depth === settings.stockfishDepth,
                // });

                const isPositionInRepo = isCurrentPositionInRepo(fen, settings, playerColor);
                addEvaluation(halfMoveNumber, {
                    evaluation: positionEvaluation,
                    fen,
                    isPositionInRepo,
                    bestMove: response.bestMove,
                    depth,
                    time,
                    lastMove,
                });
            }
            if (response.positionEvaluation && response.depth) {
                savedEvalData = {
                    positionEvaluation: response.positionEvaluation,
                    depth: response.depth,
                    bestMove: response.bestMove,
                    halfMoveNumber: response.halfMoveNumber,
                    fen: response.fen,
                    time: response.time,
                    lastMove: response.lastMove,
                    // nps: response.nps,
                };
            }
        });
    }, []);
    const handleOnMove = async (move: Move) => {
        if (state === "menu") {
            if (
                !user &&
                mode === "rated" &&
                _.isEmpty(loggedOutData.whiteAttempts) &&
                _.isEmpty(loggedOutData.blackAttempts) &&
                !storage.bufferedMove
            ) {
                storage.setBufferedMove(move);
                setShowFirstGameModal(true);
                return;
            }

            handleOnStart(false, true);
        }

        forceUpdate();
        if (!game) {
            return;
        }

        if (!_.isEmpty(arrowToDraw)) {
            setArrowToDraw(undefined);
        }

        if (game.inCheck()) {
            playCheckSound();
        } else if (move.san === "O-O" || move.san === "O-O-O") {
            playCastleSound();
        } else if (move.captured) {
            playCapturedSound();
        } else {
            playMoveSelfSound();
        }

        setMoveHistory([...game.history({ verbose: true })]);
        setRedoStack([]);

        // getStockfishEvaluation(game);

        let shouldEndRun = false;
        let endReason;
        let lichessData;
        const start = performance.now();
        lichessData = await getLichessOpeningData(game, levels, settings, playerColor, selectedPosition);
        setLichessDataState(lichessData);
        const shouldEndRunResponse = getShouldEndRun(
            lichessData,
            levels[0],
            game.moveNumber(),
            Math.ceil(game.history().length / 2),
            settings,
            game.turn(),
            playerColor,
            uniquePositionAgainstPlayer,
            selectedPosition?.engineSource,
            game,
        );
        shouldEndRun = shouldEndRunResponse.shouldEnd;
        endReason = shouldEndRunResponse.endReason;

        if (CLOUD_EVAL_ENABLED && !shouldEndRun) {
            try {
                // const { score: ev, bestMove } = await evalEngine.evaluatePositionAsync(
                //     game.fen(),
                //     settings.stockfishDepth,
                // );
                getStockfishEvaluation(game);

                // game.setComment(ev);
            } catch (error) {
                // do nothing
            }
        } else {
            // game.setComment("-");
        }

        forceUpdate();

        const end = performance.now();
        const timeTaken = end - start;

        if (state === "menu") {
            return;
        }
        if (!shouldEndRun) {
            doEngineMove(lichessData, timeTaken);
        }

        if (shouldEndRun) {
            // Set a state variable that it should end run (and then run it when stockfish is done calculating?)
            handleAttemptFinished(endReason, undefined, SLEEP_TIME_BEFORE_END_ON_PLAYERS_TURN - timeTaken);
        }
    };

    if (!selectedPosition) {
        return;
    }

    const doEngineMove = async (lichessDataAfterPlayerMove?: LichessResponse, timeTakenAfterPlayersMove?: number) => {
        if (!game) {
            return;
        }

        const start = performance.now();

        let opponentMove;

        if (isRepeatingLine || mode === "mistakes" || PGN_TO_REPEAT) {
            const repeatedGame = new Chess();

            if (isRepeatingLine) {
                repeatedGame.loadPgn(_.last(attempts)!.pgn);
            } else if (mode === "mistakes") {
                repeatedGame.loadPgn(mistakesStorage.mistakeAttempts[mistakesStorage.currentIndex].pgn);
            } else if (PGN_TO_REPEAT) {
                repeatedGame.loadPgn(PGN_TO_REPEAT);
            }

            const lastPlayerMove = game.history()[game.history().length - 1];
            const correctMove = repeatedGame.history()[game.history().length - 1];

            if (correctMove && lastPlayerMove !== correctMove) {
                setIsRepeatingLine(false);
                console.log("Aborted repetition");
            } else {
                const nextEngineMove = repeatedGame.history()[game.history().length];
                console.log("nextEngineMove:", nextEngineMove);
                opponentMove = nextEngineMove;
            }
        }

        if (
            (settings.startFromInitial && selectedPosition.pgn && !opponentMove && mode !== "mistakes") ||
            (PGN_TO_REPEAT && !opponentMove)
        ) {
            const positionGame = new Chess();
            positionGame.loadPgn(PGN_TO_REPEAT || selectedPosition.pgn!);

            const lastPlayerMove = game.history()[game.history().length - 1];
            const correctMove = positionGame.history()[game.history().length - 1];

            if (correctMove && lastPlayerMove !== correctMove) {
                const correctMoveSAN = positionGame.history({ verbose: true })[game.history().length - 1];
                setArrowToDraw({
                    from: correctMoveSAN.from,
                    to: correctMoveSAN.to,
                    shouldUndo: true,
                });
                forceUpdate();
                return;
            }

            const nextEngineMove = positionGame.history()[game.history().length];
            opponentMove = nextEngineMove;
        }

        let lichessData: LichessResponse = undefined!;
        if (!opponentMove) {
            if (lichessDataAfterPlayerMove) {
                lichessData = lichessDataAfterPlayerMove;
            } else {
                lichessData = await getLichessOpeningData(game, levels, settings, playerColor, selectedPosition);
                setLichessDataState(lichessData);
            }
            const exclusionsInThisPosition = (selectedPosition.exclusion || []).filter(
                (exclusion) => exclusion.fen === game.fen(),
            );
            const lichessOpponentMove = getOpeningBookMove(
                lichessData,
                rejectionThreshold,
                playerColor,
                settings.preferDubious,
                exclusionsInThisPosition,
            );
            opponentMove = lichessOpponentMove?.san;

            if (lichessOpponentMove) {
                opponentRatingPerMove.current = [...opponentRatingPerMove.current, lichessOpponentMove?.averageRating];
            }
        }

        if (!opponentMove) {
            setShouldReset({ shouldReset: true, reason: "novelty" });
            return;
        }

        const futureGame = new Chess();
        futureGame.loadPgn(game.pgn());
        futureGame.move(opponentMove);

        // getStockfishEvaluation(futureGame);

        let mightEndRun = false;

        if (!isRepeatingLine && lichessData) {
            const mockedLichessData = {
                white: lichessData.moves.find((move) => move.san === opponentMove)?.white || 0,
                black: lichessData.moves.find((move) => move.san === opponentMove)?.black || 0,
                draws: lichessData.moves.find((move) => move.san === opponentMove)?.draws || 0,
                isPlayerData: lichessData.isPlayerData,
            };

            mightEndRun = getShouldEndRun(
                mockedLichessData as LichessResponse,
                levels[0],
                futureGame.moveNumber(),
                futureGame.history().length / 2,
                settings,
                futureGame.turn(),
                playerColor,
                uniquePositionAgainstPlayer,
                selectedPosition.engineSource,
                game,
                true,
            ).shouldEnd;
        }

        let shouldEnd = false;
        let shouldEndReason;

        if (mightEndRun) {
            const futureLichessOpeningData = await getLichessOpeningData(
                futureGame,
                levels,
                settings,
                playerColor,
                selectedPosition,
            );

            const shouldEndResponse = getShouldEndRun(
                futureLichessOpeningData,
                levels[0],
                futureGame.moveNumber(),
                futureGame.history().length / 2,
                settings,
                futureGame.turn(),
                playerColor,
                uniquePositionAgainstPlayer,
                selectedPosition.engineSource,
                game,
            );

            shouldEnd = shouldEndResponse.shouldEnd;
            shouldEndReason = shouldEndResponse.endReason;
        }

        if (CLOUD_EVAL_ENABLED && !shouldEnd) {
            getStockfishEvaluation(futureGame);
            // try {
            //     evalEngine
            //         .evaluatePositionAsync(futureGame.fen(), settings.stockfishDepth)
            //         .then(({ score, bestMove }) => {
            //             const isPositionInRepo = isCurrentPositionInRepo(futureGame.fen(), settings, playerColor);
            //             game.setComment(score.toString());
            //             addEvaluation(futureGame.history().length, {
            //                 evaluation: score.toString(),
            //                 fen: futureGame.fen(),
            //                 isPositionInRepo,
            //                 bestMove,
            //             });
            //             addBestMove(bestMove);
            //             // Wait a bit so that this getCloudEval happens after the one when player makes move
            //             // This is to ensure correct order in evaluations
            //             // setTimeout(() => {
            //             // }, 0);
            //         });
            // } catch (error) {
            //     // do nothing
            // }
        }
        forceUpdate();

        const end = performance.now();
        const timeTaken = end - start;

        // Sleep
        const sleepTime = Math.max(ENEMY_MOVE_TIME - timeTaken - (timeTakenAfterPlayersMove || 0), 0);
        if (shouldEnd) {
            handleAttemptFinished(shouldEndReason, futureGame, sleepTime + SLEEP_TIME_BEFORE_END_ON_OPPONENTS_TURN);
        }
        await sleep(sleepTime);

        game.move(opponentMove);
        if (shouldEnd) {
            setState("attempt_finished");
        }

        setMoveHistory([...game.history({ verbose: true })]);

        const lastMove = _.last(game.history({ verbose: true }));

        if (game.inCheck()) {
            playCheckSound();
        } else if (opponentMove === "O-O" || opponentMove === "O-O-O") {
            playCastleSound();
        } else if (lastMove?.captured) {
            playCapturedSound();
        } else {
            playMoveOpponentSound();
        }

        forceUpdate();
    };

    const handleOnStart = async (isRepeatingCurrentLine?: boolean, skipReset?: boolean) => {
        if (_.isEmpty(levels) && mode === "practice") {
            alert("No opponent level selected");
            return;
        }

        if (!selectedPosition.standard && !selectedPosition.isInitialPosition) {
            await paywall(user);
        }

        if (settings.preferDubious && _.intersection(["2500", "Masters"], levels).length > 0) {
            alert("Trappy/Dubious moves setting is only available for opponent levels up to 2200");
            return;
        }

        if (isRepeatingCurrentLine || mode === "mistakes") {
            setIsUnrated(true);
        } else {
            setIsUnrated(false);
            setIsRepeatingLine(false);
        }

        if (state !== "attempt_finished" && shouldReset.reason !== "wrongMove") {
            setAttempts([]);
        }

        if ((mode === "rated" || mode === "mistakes") && settings.isRated) {
            const engineLevel = getEngineLevelForPosition(selectedPosition.rating || 1000);
            setLevels([engineLevel]);
        }
        setRatingGain(undefined);
        setRedoStack([]);
        setMoveHistory([]);
        setEvaluations({});
        setBestMoves([]);
        setUniquePositionAgainstPlayer(false);
        opponentRatingPerMove.current = [];
        if (!skipReset) {
            handleOnResetPosition();
        }
        setState("playing");

        if (settings.startFromInitial) {
            game?.load(initialFen);
        }

        const newGame = new Chess();
        newGame.loadPgn(selectedPosition.pgn || "");
        setMoveHistory([...newGame?.history({ verbose: true })!]);

        if (settings.startFromInitial) {
            const newGame2 = new Chess();
            newGame2?.load(initialFen);
            setGame(newGame2);
            setMoveHistory([]);
        }
    };

    const handleAttemptFinished = async (endReason?: EndReason, futureGame?: Chess, sleepTime?: number) => {
        try {
            if (!selectedPosition || !selectedPosition.rating) {
                Sentry.captureException(new Error("No selected position or rating"), {
                    extra: { game: game?.pgn(), selectedPosition, evaluations, playerColor, mode, settings },
                });
                return;
            }

            const game2 = futureGame || game;

            // const cloudEval = game2?.getComment();
            // let ev;
            // if (cloudEval && cloudEval !== "-") {
            //     ev = cloudEval;
            //     console.log("used cloudEval:", cloudEval);
            // } else {
            //     ev = await getStockfishEval(game2?.fen()!, playerColor);
            //     console.log("used stockfish eval:", ev);
            // }
            const start = performance.now();
            const {
                score: evStr,
                bestMove,
                time,
                depth,
            } = await evalEngine.evaluatePositionAsync(game2?.fen()!, settings.stockfishDepth2);
            const ev = endReason === "threefoldRepetition" ? 0 : parseFloat(evStr);
            // console.log("Time taken:", performance.now() - start, "ms");
            console.log("ev from Local stockfish:", ev);
            // const start2 = performance.now();
            // const ev2 = await getStockfishEval(game2?.fen()!);
            // console.log("Time taken:", performance.now() - start2, "ms");
            // console.log("ev from api:", ev2);

            game?.setComment(ev.toString());
            const isPositionInRepo = isCurrentPositionInRepo(game2?.fen()!, settings, playerColor);
            addEvaluation(game2?.history().length!, {
                evaluation: ev.toString(),
                fen: game2?.fen()!,
                bestMove,
                isPositionInRepo,
                depth,
                time,
                lastMove: _.last(game2?.history())!,
            });
            const attemptType = getAttemptType(playerColor, ev);

            let opponentRating;
            if (levels[0] === "Masters") {
                opponentRating = 2600;
            } else {
                opponentRating = !_.isEmpty(opponentRatingPerMove.current)
                    ? Math.round(_.mean(opponentRatingPerMove.current))
                    : parseInt(levels[0]);
            }

            const attempt: Attempt = {
                pgn: game2?.pgn()!,
                evaluation: ev,
                type: attemptType,
                endReason,
                engineLevel: opponentRating || selectedPosition.rating,
                positionRating: selectedPosition.rating || 1199,
                playerColor,
                ot_positionId: selectedPosition.id!,
                timeControls: settings.timeControls,
                rejectionThreshold: rejectionThreshold,
                gameDuration: settings.gameDuration,
            };
            const newAttempts = [...attempts, attempt];
            setAttempts(newAttempts);

            if (!isUnrated) {
                let newRating;
                let ratingGain;

                if (user) {
                    postAttempt(
                        attempt,
                        opponentRating || selectedPosition.rating,
                        selectedPosition.rating || 1199,
                        playerColor,
                        selectedPosition.id,
                        selectedPosition.startRating,
                        settings.timeControls,
                        rejectionThreshold,
                        settings.gameDuration,
                        settings.isRated !== false && mode !== "practice",
                        levels[0],
                    );
                    ratingGain = getRatingGain(
                        selectedPosition.rating!,
                        opponentRating || selectedPosition.rating,
                        attempt.evaluation,
                        playerColor,
                        64,
                        null,
                    );

                    newRating = selectedPosition.rating! + ratingGain;
                } else {
                    let kFactor = 64;
                    if (playerColor === "w") {
                        kFactor = getKFactor(loggedOutData.whiteAttempts.length);
                    } else {
                        kFactor = getKFactor(loggedOutData.blackAttempts.length);
                    }

                    ratingGain = getRatingGain(
                        selectedPosition.rating!,
                        opponentRating || selectedPosition.rating,
                        attempt.evaluation,
                        playerColor,
                        kFactor,
                        null,
                    );
                    newRating = selectedPosition.rating! + ratingGain;

                    const attemptToSave = {
                        ...attempt,
                        ratingGain,
                    };

                    if (!user && mode === "rated") {
                        if (playerColor === "w") {
                            setLoggedOutData({
                                ...loggedOutData,
                                whiteRating: newRating,
                                whiteAttempts: [...loggedOutData.whiteAttempts, attemptToSave],
                            });
                        } else {
                            setLoggedOutData({
                                ...loggedOutData,
                                blackRating: newRating,
                                blackAttempts: [...loggedOutData.blackAttempts, attemptToSave],
                            });
                        }
                    }
                }
                if (settings.isRated && mode === "rated") {
                    setRatingGain(ratingGain);
                    setPositions(
                        (prevPositions) =>
                            prevPositions?.map((pos) => {
                                if (pos.id === selectedPosition.id) {
                                    return {
                                        ...pos,
                                        rating: newRating,
                                    };
                                }
                                return pos;
                            }),
                    );
                }
            }

            const end = performance.now();
            const timeTaken = end - start;

            if (sleepTime) {
                await sleep(Math.max(sleepTime - timeTaken, 0));
            }

            if ((ev >= EQUALIBRIUM && playerColor === "w") || (ev <= EQUALIBRIUM && playerColor === "b")) {
                playSuccessSound();
            }
            setState("attempt_finished");
            console.log("evaluations:", evaluations);
        } catch (error) {
            Sentry.captureException(error, {
                extra: { game: game?.pgn(), selectedPosition, evaluations, playerColor, mode, settings },
            });
        }
    };

    const handleOnResetPosition = () => {
        const newGame = new Chess(initialFen);
        if (selectedPosition.pgn) {
            newGame?.loadPgn(selectedPosition.pgn);
            setGame(newGame);
            setPlayerColor(selectedPosition.defaultPlayerColor || "w");
        } else if (selectedPosition.fen) {
            newGame?.load(selectedPosition.fen);
            setGame(newGame);
            setPlayerColor(selectedPosition.defaultPlayerColor || "w");
        }

        if (!_.isEmpty(arrowToDraw)) {
            setArrowToDraw(undefined);
        }

        setShouldReset({ shouldReset: false });
    };

    const handleOnBack = () => {
        setState("menu");
        handleOnResetPosition();
    };

    const handleSetSelectedPosition = (position: Position) => {
        if (position.name === "Custom position" && isMobile) {
            setState("choose_custom_position");
        }
        setSelectedPositionID(position.id);
    };

    const handleOnRepeatLine = () => {
        setIsRepeatingLine(true);
        handleOnStart(true);
    };

    const getStockfishEvaluation = async (game: Chess) => {
        // if (!settings.useLocalStockfish) {
        //     return;
        // }

        const stockfishLevel = settings.stockfishDepth2;
        const searchMoves = game.history({ verbose: true }).map((move) => move.lan);

        const searchMovesString = searchMoves.length > 0 ? ` moves ${searchMoves.join(" ")}` : "";
        engine.evaluatePosition(
            initialFen,
            game.fen(),
            searchMovesString,
            stockfishLevel,
            game.turn(),
            game.history().length,
            _.last(game.history()),
        );
    };

    if (!game) {
        return;
    }

    if (!width) {
        return;
    }

    const isMobile = width < 1024;

    return (
        <div>
            {/* <Alert
                variant="light"
                color="yellow"
                title="26. May: There are some problems with engine not responding for Opening Trainer. We are working on fixing this."
                icon={<IconTriangle size="1rem" />}
            ></Alert> */}
            {user?.username === "Grizzly" && (
                <Alert
                    variant="light"
                    color="yellow"
                    title="Hi Grizzly! I have tried responding to your emails but seems like they end up in your spam folder or something. Please reach out on Discord or try sending email with another email address. Right now your user has no password."
                    icon={<IconTriangle size="1rem" />}
                ></Alert>
            )}

            {(!isMobile || state === "menu") && (
                <Header user={user} selected="play" onClickPlay={() => setState("menu")} />
            )}

            <div
                className={cn("flex justify-center max-[1023px]:flex-col [@media(min-width:1024px)]:mt-10", {
                    "[@media(max-width:1023px)]:mt-16": state !== "menu",
                    "[@media(max-width:400px)]:mt-32": state !== "menu",
                })}
            >
                {/* {!isMobile && (
                    <Affix className="flex w-72" position={{ top: rem(20), left: rem(20) }}>
                        <Image src="/logo.png" alt="Chessable logo" width={100} height={100} />
                    </Affix>
                )} */}
                <FirstGameModal
                    onStart={() => {
                        if (storage.bufferedMove) {
                            handleOnMove(storage.bufferedMove as Move);
                            storage.setBufferedMove(undefined);
                        } else {
                            handleOnStart();
                        }
                    }}
                />
                {/* {user?.isPremium && <NewFeatureModal />} */}
                {/* {!isMobile && (
                    <Affix position={{ bottom: rem(20), right: rem(20) }}>
                        <Tooltip label="Discord">
                            <a href="https://discord.gg/WNytKVEa8S">
                                <ActionIcon variant="transparent" size="xl">
                                    <ThemeIcon variant="default" size={40} radius="xl">
                                        <IconBrandDiscordFilled />
                                    </ThemeIcon>
                                </ActionIcon>
                            </a>
                        </Tooltip>
                    </Affix>
                )} */}
                <Affix position={{ top: rem(20), right: rem(20) }}>
                    {isMobile && isInPlayingState(state) && <Burger onClick={() => setState("menu")} />}
                </Affix>
                {(!isMobile || state !== "menu") && (
                    <Chessboard
                        game={game}
                        onMove={handleOnMove}
                        playerColor={playerColor}
                        result={undefined}
                        state={state}
                        enablePremoves={isInPlayingState(state) && _.isEmpty(redoStack)}
                        user={user}
                        mode={mode}
                        setState={setState}
                    />
                )}
                {state === "choose_custom_position" && (
                    <Button className="!h-20 my-6" color="green" onClick={() => setState("menu")}>
                        Set custom position
                    </Button>
                )}
                {(state === "menu" || (isInPlayingState(state) && !isMobile)) && (
                    <ContextMenu className="bg-[#25262b] w-[300px] max-lg:w-[100vw] max-lg:h-[100vh] p-6" game={game}>
                        {state === "menu" && (
                            <MenuPanel
                                selectedPosition={selectedPosition}
                                setSelectedPosition={handleSetSelectedPosition}
                                onStart={handleOnStart}
                                settings={settings}
                                user={user}
                                setMode={setMode}
                                mode={mode}
                                positionsToDisplay={positionsToDisplay}
                            />
                        )}
                        {isInPlayingState(state) && (
                            <PlayingPanel
                                game={game}
                                onBack={handleOnBack}
                                resetPosition={() => {
                                    setIsRepeatingLine(false);
                                    handleOnStart();
                                }}
                                shouldReset={shouldReset}
                                settings={settings}
                                state={state}
                                mode={mode}
                                setState={setState}
                                level={levels[0]}
                                ratingGain={ratingGain}
                                user={user}
                                onRepeatLine={handleOnRepeatLine}
                                setMode={setMode}
                                lichessData={lichessDataState}
                                selectedPosition={selectedPosition}
                            />
                        )}
                    </ContextMenu>
                )}
                {isInPlayingState(state) && isMobile && (
                    <PlayingPanelMobile
                        game={game}
                        onBack={handleOnBack}
                        resetPosition={() => {
                            setIsRepeatingLine(false);
                            handleOnStart();
                        }}
                        shouldReset={shouldReset}
                        state={state}
                        mode={mode}
                        setState={setState}
                        level={levels[0]}
                        ratingGain={ratingGain}
                        user={user}
                        onRepeatLine={handleOnRepeatLine}
                        selectedPosition={selectedPosition}
                    />
                )}
                {/* <ProductDisplay /> */}
                <PurchaseConfirmationModal />
            </div>
            {/* {isInPlayingState(state) && mode === "ranked" && (
                <div className="w-72 mx-10 md:w-96 md:mx-20 mt-10 block sm:hidden">
                    <Attempts />
                </div>
            )} */}
        </div>
    );
};

export default App;
